import {appConfig} from "../../config";
import {apiRequest} from "../../util/ApiUtils";
import {Employee} from "../EmployeeApi";
import {ProductGroupMap, TeamProductGroupMap} from "./Types";
import {UploadDataUploadResponse, MonthlyDataUploadRequest} from "./PerformanceApi";
import {BaseCompensation} from "../CompensationApi";

export interface CommissionTransaction {
    contractId: number;
    commissionId: number;
    accountingType: 'CREDIT' | 'RECLAIM' | string;
    orderNumberNae?: number;
    orderNaeEntryDate?: string;
    orderIdSacos?: number;
    customerId?: string;
    customerName?: string;
    agentId?: string;
    productId?: number;
    productDescription?: string;
    commissionDescription?: string;
    sapProductGroup?: number;
    sapProductGroupDescription?: string;
    productPositionId?: number;
    productPositionDescription?: string;
    commissionType?: 'SUPERDEAL' | 'BASE_COMMISSION' | string;
    currency?: string;
    cancellationReason?: string;
    cancellationDate?: string;
    commissionNettoValue?: string;
    invoicePositionId?: number;
    campaignDescription?: string;
    mediumCode?: string;
    dslOrderId?: number;
    commissionStatus?: 'BEFORE_RECLAIM' | 'PAID_OUT' | 'RECLAIMED' | string;
    isTarifChange?: boolean;
    isOnFeature?: boolean;
    isNewOrder?: boolean;
    reportYear: number;
    reportMonth: number;
}

export interface CommissionPayoffPeriod {
    id: number;
    year: number;
    month: number;
    state: 'PREVIEW' | 'CREATED' | 'IN_REVIEW' | 'FINALIZED' | 'RELEASED';
    commissionPayoffs: CommissionPayoff[];
    totalGrossCommission: number;
    totalAgents: number;
    totalPoints: number;
    totalOrders: number;
    pointsByProductGroup: ProductGroupMap;
    pointsByTeamAndProductGroup: TeamProductGroupMap;
    transactionsByTeamAndProductGroup: TeamProductGroupMap;
    transactionsByProductGroup: ProductGroupMap;
}

export interface CommissionPayoff {
    id: number;
    agent: Employee;
    cancellationRate: string;
    commissionPayoffItems: CommissionPayoffItem[];
    demandReached: boolean;
    overridden: boolean;
    suspended: boolean;
    totalPoints: number;
    totalPaidTransactions: number;
    grossCommission: string;
    grossCommissionWithTarget: string;
    pointsByProductGroup: ProductGroupMap;
    transactionCountByProductGroup: ProductGroupMap;
    compensation: BaseCompensation;
}

export interface CommissionPayoffItem {
    id: number;
    productGroup: string;
    paidCommission: string;
    paidAddition: string;
    points: string;
    payOut: string;
    commissionDescription: string;
    contractNumber: string;
    commissionNettoValue: string;
}

export interface CommissionCategory {
    id: number;
    categoryName: string;
    productGroup: string;
    earnedPoints: string;
    paidCommission: boolean;
    commissionProducts: CommissionProduct[];
}

export interface CommissionProduct {
    id: number;
    commissionDescription: string;
    earnedCommission: string;
    salesGroup: string;
    isActive: boolean;
}

export interface CommissionTransactionFetchRequest {
    page?: number;
}

export interface CommissionTransactionFetchResponse {
    transactions: CommissionTransaction[];
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
}

export interface CommissionPayoffPeriodUpdateRequest {
    id: number;
    newState: 'PREVIEW' | 'CREATED' | 'IN_REVIEW' | 'FINALIZED';
    payrollCycle?: string;
}

export interface CommissionPayoffPeriodResetRequest {
    id: number;
}

export interface AssignCommissionProductRequest {
    commissionProductId: number;
}


const backendUrl = appConfig.backendUrl;

/**
 * Fetches commission transactions with optional pagination
 */
export async function fetchCommissionTransactions(request: CommissionTransactionFetchRequest = {}): Promise<CommissionTransactionFetchResponse> {

    const queryParams = new URLSearchParams();
    if (request.page !== undefined) {
        queryParams.append('page', request.page.toString());
    }

    const data = await apiRequest(`${backendUrl}/api/custom/commissions/transactions?${queryParams.toString()}`,
        {
            method: "GET",
        }
    );

    return data as CommissionTransactionFetchResponse;
}

/**
 * Recreates a payoff period for the specified year and month
 */
export async function createPayoffPeriod(year: number, month: number): Promise<CommissionPayoffPeriod> {

    const data = await apiRequest(`${backendUrl}/api/custom/commissions/payoffs/${year}/${month}`,
        {
            method: "POST"
        }
    );

    return data as CommissionPayoffPeriod;
}

/**
 * Fetches commission payoff period for a specific year and month
 */
export async function fetchCommissionPayoffPeriod(year: number, month: number): Promise<CommissionPayoffPeriod> {

    const data = await apiRequest(`${backendUrl}/api/custom/commissions/payoffs/${year}/${month}`,
        {
            method: "GET",
        }
    );

    return data as CommissionPayoffPeriod;
}

export async function fetchCommissionPayoff(year: string, month: string, employeeId: string): Promise<CommissionPayoff> {

    const data = await apiRequest(`${backendUrl}/api/custom/commissions/payoffs/${year}/${month}/${employeeId}`,
        {
            method: "GET",
        }
    );

    return data as CommissionPayoff;
}

export async function fetchCommissionCategories(): Promise<CommissionCategory[]> {

    const data = await apiRequest(`${backendUrl}/api/custom/commissions/commission-categories`,
        {
            method: "GET",
        }
    );

    return data as CommissionCategory[];
}

export async function assignCommissionProduct(id: number, request: AssignCommissionProductRequest): Promise<CommissionCategory> {

    const data = await apiRequest(`${backendUrl}/api/custom/commissions/commission-categories/${id}`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(request)
        }
    );

    return data as CommissionCategory;
}

export async function unassignCommissionProduct(categoryId: number, productId: number): Promise<void> {

    await apiRequest(`${backendUrl}/api/custom/commissions/commission-categories/${categoryId}/products/${productId}`,
        {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            }
        }
    );

    return;
}

export async function fetchCommissionProducts(): Promise<CommissionProduct[]> {

    const data = await apiRequest(`${backendUrl}/api/custom/commissions/commission-products`,
        {
            method: "GET",
        }
    );

    return data as CommissionProduct[];
}

/**
 * Recreates a payoff period for the specified year and month
 */
export async function recreatePayoffPeriod(year: number, month: number): Promise<CommissionPayoffPeriod> {

    const queryParams = new URLSearchParams();
    queryParams.append('year', year.toString());
    queryParams.append('month', month.toString());

    const data = await apiRequest(`${backendUrl}/api/custom/commissions/payoffs?${queryParams.toString()}`,
        {
            method: "PUT",
        }
    );

    return data as CommissionPayoffPeriod;
}

/**
 * Updates the state of a commission payoff period
 */
export async function updateCommissionPayoffPeriod(request: CommissionPayoffPeriodUpdateRequest): Promise<CommissionPayoffPeriod> {

    const data = await apiRequest(`${backendUrl}/api/custom/commissions/payoffs/${request.id}`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(request)
        }
    );

    return data as CommissionPayoffPeriod;
}

export async function releaseCommissionPayoffPeriod(id: number, payrollCycle: string): Promise<CommissionPayoffPeriod> {

    const data = await apiRequest(`${backendUrl}/api/custom/commissions/payoffs/${id}/release`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({payrollCycle: payrollCycle})
        }
    );

    return data as CommissionPayoffPeriod;
}

/**
 * Resets a commission payoff period to its initial state
 */
export async function resetCommissionPayoffPeriod(request: CommissionPayoffPeriodResetRequest): Promise<CommissionPayoffPeriod> {

    const data = await apiRequest(`${backendUrl}/api/custom/commissions/payoffs/${request.id}`,
        {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(request)
        }
    );

    return data as CommissionPayoffPeriod;
}

export async function overrideCommissionPayoff(payoffPeriodId: number, employeeId: string): Promise<CommissionPayoffPeriod> {

    const data = await apiRequest(`${backendUrl}/api/custom/commissions/payoffs/${payoffPeriodId}/${employeeId}/override`,
        {
            method: "PUT"
        }
    );

    return data as CommissionPayoffPeriod;
}

export async function undoOverrideCommissionPayoff(payoffPeriodId: number, employeeId: string): Promise<CommissionPayoffPeriod> {

    const data = await apiRequest(`${backendUrl}/api/custom/commissions/payoffs/${payoffPeriodId}/${employeeId}/override/undo`,
        {
            method: "PUT"
        }
    );

    return data as CommissionPayoffPeriod;
}

export async function suspendCommissionPayoff(payoffPeriodId: number, employeeId: string): Promise<CommissionPayoffPeriod> {

    const data = await apiRequest(`${backendUrl}/api/custom/commissions/payoffs/${payoffPeriodId}/${employeeId}/suspend`,
        {
            method: "PUT"
        }
    );

    return data as CommissionPayoffPeriod;
}

export async function undoSuspendCommissionPayoff(payoffPeriodId: number, employeeId: string): Promise<CommissionPayoffPeriod> {

    const data = await apiRequest(`${backendUrl}/api/custom/commissions/payoffs/${payoffPeriodId}/${employeeId}/suspend/undo`,
        {
            method: "PUT"
        }
    );

    return data as CommissionPayoffPeriod;
}

/**
 * Imports commission transactions from a file
 */
export async function importCommissionTransactions(request: MonthlyDataUploadRequest): Promise<UploadDataUploadResponse> {

    const formData = new FormData();
    formData.append('file', request.file!);
    formData.append('reportMonth', request.reportMonth);

    const response = await fetch(`${backendUrl}/api/custom/commissions/commission-transactions`,
        {
            method: 'POST',
            body: formData,
            credentials: 'include',
        }
    );

    if (!response.ok) {
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }

    return await response.json() as UploadDataUploadResponse;
}